import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * Comparison slider component for comparing two images
 *
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} mediaWrapper - The container for the images
 * @property {HTMLInputElement} slider - The range input element
 * @property {HTMLElement} afterImage - The image that gets revealed
 * @property {HTMLElement} handle - The draggable circular handle
 *
 * @extends {Component<ComparisonSliderRefs>}
 *
 * @property {string[]} requiredRefs - Required refs: 'mediaWrapper', 'slider', 'afterImage', and 'handle'
 */
export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage', 'handle'];

  constructor() {
    super();
    this.hasAnimated = false;
    this.boundHandleIntersection = this.handleIntersection.bind(this);
  }

  /**
   * Called when component is added to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper } = this.refs;

    // Get orientation from media wrapper
    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';

    // Initialize the position
    this.sync();

    // Let the handle be dragged in the perpendicular axis too (e.g. up/down
    // for a horizontal slider) so it can be moved out of the way of the
    // image underneath — purely visual, resets to centered on reload.
    this.setupHandleDrag();

    // Set up intersection observer for animation
    this.setupIntersectionObserver();
  }

  /**
   * Sync the CSS custom property with the input value
   */
  sync() {
    const { mediaWrapper, slider } = this.refs;
    // Skip sync during animation to prevent lag
    if (this.isAnimating) return;

    const val = (Number(slider.value) - Number(slider.min)) / (Number(slider.max) - Number(slider.min));
    const compareValue = Math.round(val * 1000) / 10;

    // Set the CSS custom property on the media wrapper
    mediaWrapper.style.setProperty('--compare', String(compareValue));
  }

  /**
   * Free 2-axis dragging on the handle itself: the axis matching the
   * slider's own orientation still drives --compare (the before/after
   * split), exactly as dragging the track already does. The perpendicular
   * axis is new — it only moves the handle visually along the divider
   * line via --handle-offset, with no effect on the split. Nothing here
   * persists: --handle-offset is a runtime inline style, so every fresh
   * connectedCallback (i.e. every page load) starts back at the CSS
   * fallback (50, centered).
   */
  setupHandleDrag() {
    const { mediaWrapper, slider, handle } = this.refs;
    if (!handle) return;

    let pointerId = null;

    const updateFromEvent = (event) => {
      const rect = mediaWrapper.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const xPct = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const yPct = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));

      const comparePct = this.orientation === 'vertical' ? 100 - yPct : xPct;
      slider.value = String(comparePct);
      this.sync();

      const offsetPct = this.orientation === 'vertical' ? xPct : yPct;
      mediaWrapper.style.setProperty('--handle-offset', String(Math.round(offsetPct)));
    };

    handle.addEventListener('pointerdown', (event) => {
      pointerId = event.pointerId;
      handle.setPointerCapture(pointerId);
      event.preventDefault();
      updateFromEvent(event);
    });

    handle.addEventListener('pointermove', (event) => {
      if (pointerId === null) return;
      updateFromEvent(event);
    });

    const endDrag = () => {
      if (pointerId === null) return;
      try {
        handle.releasePointerCapture(pointerId);
      } catch (error) {
        // Capture may already be released (e.g. pointercancel) — ignore.
      }
      pointerId = null;
    };
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);
  }

  /**
   * Clean up when component is removed
   */
  disconnectedCallback() {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Set the slider value and update display
   * @param {number} value - Value between 0-100 (0 = all after, 100 = all before)
   */
  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  /**
   * Animate the slider handle to give users a hint about the interaction
   */
  animateSlider() {
    const { mediaWrapper, slider } = this.refs;
    if (this.hasAnimated) return;

    this.hasAnimated = true;
    this.isAnimating = true;

    // Enable transition for smooth animation
    mediaWrapper.style.setProperty('--transition-duration', '0.5s');

    // Create a subtle sliding animation by only setting CSS property
    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '40');
    }, 100);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '60');
    }, 600);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '50');
    }, 1100);

    setTimeout(() => {
      // Remove transition after animation and sync slider value
      mediaWrapper.style.setProperty('--transition-duration', '0s');
      // Sync the slider value to match the final position
      slider.value = '50';
      this.isAnimating = false;
    }, 1600);
  }

  /**
   * Set up intersection observer to detect when section comes into view
   */
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of the component is visible
    };

    this.intersectionObserver = new IntersectionObserver(this.boundHandleIntersection, options);
    this.intersectionObserver.observe(this);
  }

  /**
   * Handle intersection observer callback
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        // Add a small delay to ensure everything is rendered
        setTimeout(() => {
          oncePerEditorSession(this, `comparison-slider-animated`, () => {
            this.animateSlider();
          });
        }, 300);

        // Disconnect observer after first animation
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    });
  }
}

// Register the custom element
if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}
